import React, { useState, useEffect } from "react";
import {
    Box, Button, Chip, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, Divider, IconButton, List, ListItem,
    ListItemAvatar, ListItemText, Paper, Stack, TextField, Tooltip,
    Typography, Alert, Snackbar, Avatar, Collapse
} from "@mui/material";
import {
    Close as CloseIcon, History as HistoryIcon, Refresh as RefreshIcon,
    Phone as PhoneIcon, Edit as EditIcon, CheckCircle as CheckCircleIcon,
    Error as ErrorIcon, Warning as WarningIcon, Send as SendIcon,
    WhatsApp as WhatsAppIcon, Schedule as ScheduleIcon, Person as PersonIcon,
    ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon
} from "@mui/icons-material";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useWhatsappNotifications } from "../../../hooks/whatsapp/useWhatsappNotifications";

const NotificationStatusIcon = ({ status }) => {
    switch (status) {
        case 'ENVIADO': return <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />;
        case 'FALLIDO': return <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />;
        case 'PENDIENTE': return <ScheduleIcon sx={{ color: 'warning.main', fontSize: 20 }} />;
        default: return <WarningIcon sx={{ color: 'grey.500', fontSize: 20 }} />;
    }
};

const StatusChip = ({ status }) => {
    const config = {
        ENVIADO: { color: 'success', label: 'Enviado' },
        FALLIDO: { color: 'error', label: 'Fallido' },
        PENDIENTE: { color: 'warning', label: 'Pendiente' },
        LEIDO: { color: 'info', label: 'Leído' },
    };
    const { color, label } = config[status] || { color: 'default', label: status };
    return <Chip size="small" color={color} label={label} />;
};

const NotificationTypeChip = ({ type }) => {
    const config = {
        SOLICITUD_PROCESANDO: { color: 'info', label: 'Solicitud' },
        VEHICULO_ASIGNADO: { color: 'success', label: 'Vehículo asignado' },
        RECORDATORIO: { color: 'warning', label: 'Recordatorio' },
        COMPLETADO: { color: 'primary', label: 'Completado' },
        ERROR: { color: 'error', label: 'Error' },
    };
    const { color, label } = config[type] || { color: 'default', label: type };
    return <Chip size="small" variant="outlined" color={color} label={label} />;
};

const PassengerNotificationHistory = ({
    manifestId,
    passengerId,
    passengerName,
    phoneNumber,
    open,
    onClose
}) => {
    const { loading, error, fetchHistory, retryFailed, updatePhone } = useWhatsappNotifications(manifestId);
    const [notifications, setNotifications] = useState([]);
    const [editingPhone, setEditingPhone] = useState(false);
    const [newPhone, setNewPhone] = useState(phoneNumber || '');
    const [successMessage, setSuccessMessage] = useState(null);
    const [retrying, setRetrying] = useState(false);
    const [retryingSingle, setRetryingSingle] = useState(null); // id de notificación que se está reenviando
    const [localError, setLocalError] = useState(null);
    const [expandedError, setExpandedError] = useState(null);

    const loadHistory = async () => {
        if (!manifestId) return;
        try {
            const data = await fetchHistory(passengerId);
            setNotifications(data || []);
        } catch (err) {
            setLocalError('Error al cargar el historial de notificaciones');
            console.error(err);
        }
    };

    useEffect(() => {
        if (open && manifestId) {
            loadHistory();
        }
        if (!open) {
            setEditingPhone(false);
            setNewPhone(phoneNumber || '');
        }
    }, [open, manifestId, passengerId]);

    const handleRetryAll = async () => {
        setRetrying(true);
        setLocalError(null);
        try {
            const result = await retryFailed(); // sin perId
            setSuccessMessage(`Se reenviaron ${result.retried} notificaciones exitosamente`);
            await loadHistory();
        } catch (err) {
            setLocalError('Error al reenviar todas las notificaciones');
            console.error(err);
        } finally {
            setRetrying(false);
        }
    };

    const handleRetrySingle = async (notifId) => {
        setRetryingSingle(notifId);
        setLocalError(null);
        try {
            // Necesitamos pasar perId al backend para reenviar solo las de ese pasajero
            await retryFailed(passengerId);
            setSuccessMessage(`Notificación reenviada exitosamente`);
            await loadHistory();
        } catch (err) {
            setLocalError('Error al reenviar la notificación');
            console.error(err);
        } finally {
            setRetryingSingle(null);
        }
    };

    const handleUpdatePhone = async () => {
        if (!newPhone || newPhone.length < 10) {
            setLocalError('El número de teléfono debe tener al menos 10 dígitos');
            return;
        }
        try {
            await updatePhone(passengerId, newPhone);
            setSuccessMessage('Número de teléfono actualizado exitosamente');
            setEditingPhone(false);
            await loadHistory();
        } catch (err) {
            setLocalError('Error al actualizar el número de teléfono');
            console.error(err);
        }
    };

    const getFailedCount = () => notifications.filter(n => n.whanotest === 'FALLIDO').length;
    const getPendingCount = () => notifications.filter(n => n.whanotest === 'PENDIENTE').length;

    const toggleError = (id) => {
        setExpandedError(prev => prev === id ? null : id);
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
                PaperProps={{ sx: { borderRadius: 3, maxHeight: '80vh' } }}>
                <DialogTitle sx={{ pb: 1 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Avatar sx={{ bgcolor: 'success.main' }}><WhatsAppIcon /></Avatar>
                            <Box>
                                <Typography variant="h6" fontWeight="bold">Historial de Notificaciones</Typography>
                                <Typography variant="body2" color="text.secondary">{passengerName || 'Pasajero'}</Typography>
                            </Box>
                        </Stack>
                        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
                    </Stack>
                </DialogTitle>

                <DialogContent dividers>
                    <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: 'grey.50' }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                            <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}><PersonIcon sx={{ fontSize: 32 }} /></Avatar>
                            <Box flex={1}>
                                <Typography variant="subtitle1" fontWeight="bold">{passengerName || 'Sin nombre'}</Typography>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                    <PhoneIcon fontSize="small" color="action" />
                                    {editingPhone ? (
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                            <TextField size="small" value={newPhone} onChange={(e) => setNewPhone(e.target.value)}
                                                placeholder="Número de teléfono" autoFocus sx={{ width: 180 }} />
                                            <Button size="small" variant="contained" onClick={handleUpdatePhone}>Guardar</Button>
                                            <Button size="small" onClick={() => setEditingPhone(false)}>Cancelar</Button>
                                        </Stack>
                                    ) : (
                                        <>
                                            <Typography variant="body2">{phoneNumber || 'No registrado'}</Typography>
                                            <Tooltip title="Editar número">
                                                <IconButton size="small" onClick={() => setEditingPhone(true)}><EditIcon fontSize="small" /></IconButton>
                                            </Tooltip>
                                        </>
                                    )}
                                </Stack>
                            </Box>
                            <Stack direction="row" spacing={1}>
                                <Chip size="small" color="primary" label={`${notifications.length} notificaciones`} />
                                {getFailedCount() > 0 && <Chip size="small" color="error" label={`${getFailedCount()} fallidas`} />}
                                {getPendingCount() > 0 && <Chip size="small" color="warning" label={`${getPendingCount()} pendientes`} />}
                            </Stack>
                        </Stack>
                    </Paper>

                    <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" useFlexGap>
                        <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={loadHistory} disabled={loading}>Actualizar</Button>
                        {getFailedCount() > 0 && (
                            <Button size="small" variant="contained" color="warning" startIcon={<SendIcon />}
                                onClick={handleRetryAll} disabled={retrying || loading}>
                                {retrying ? 'Reenviando...' : `Reenviar ${getFailedCount()} fallidas`}
                            </Button>
                        )}
                    </Stack>

                    {loading ? (
                        <Box display="flex" justifyContent="center" py={5}><CircularProgress /></Box>
                    ) : notifications.length === 0 ? (
                        <Alert severity="info" sx={{ borderRadius: 2 }}>No hay notificaciones registradas para este pasajero.</Alert>
                    ) : (
                        <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                            {notifications.map((notif, index) => {
                                const isFailed = notif.whanotest === 'FALLIDO';
                                return (
                                    <React.Fragment key={notif.whanotid || index}>
                                        <ListItem
                                            alignItems="flex-start"
                                            sx={{
                                                py: 2,
                                                px: 2,
                                                borderRadius: 2,
                                                bgcolor: 'transparent', // Sin fondo de color
                                                border: isFailed ? '1px solid' : 'none',
                                                borderColor: 'error.main',
                                                '&:hover': { bgcolor: 'action.hover' },
                                                flexDirection: 'column',
                                                alignItems: 'stretch',
                                            }}
                                        >
                                            <Stack direction="row" spacing={2} alignItems="flex-start" width="100%">
                                                <ListItemAvatar sx={{ minWidth: 40 }}>
                                                    <Avatar sx={{
                                                        bgcolor: 'transparent',
                                                        border: isFailed ? '1px solid' : 'none',
                                                        borderColor: 'error.main',
                                                    }}>
                                                        <NotificationStatusIcon status={notif.whanotest} />
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={
                                                        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap">
                                                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                                                <NotificationTypeChip type={notif.whanottip} />
                                                                <StatusChip status={notif.whanotest} />
                                                                {isFailed && (
                                                                    <Button size="small" variant="outlined" color="error"
                                                                        startIcon={retryingSingle === notif.whanotid ? <CircularProgress size={14} /> : <RefreshIcon />}
                                                                        onClick={() => handleRetrySingle(notif.whanotid)}
                                                                        disabled={retryingSingle !== null}>
                                                                        Reenviar
                                                                    </Button>
                                                                )}
                                                            </Stack>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {notif.credat && format(new Date(notif.credat), "dd/MM/yyyy HH:mm", { locale: es })}
                                                            </Typography>
                                                        </Stack>
                                                    }
                                                    secondary={
                                                        <Box mt={1}>
                                                            {notif.whanotmen && (
                                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                                                                    {notif.whanotmen.length > 200 ? `${notif.whanotmen.substring(0, 200)}...` : notif.whanotmen}
                                                                </Typography>
                                                            )}
                                                            {isFailed && notif.whanotlasterror && (
                                                                <Box sx={{ mt: 1 }}>
                                                                    <Button size="small" endIcon={expandedError === notif.whanotid ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                                        onClick={() => toggleError(notif.whanotid)} color="error" variant="text">
                                                                        {expandedError === notif.whanotid ? 'Ocultar error' : 'Ver error'}
                                                                    </Button>
                                                                    <Collapse in={expandedError === notif.whanotid}>
                                                                        <Alert severity="error" sx={{ mt: 1, py: 0.5, fontSize: '0.75rem' }}>
                                                                            {notif.whanotlasterror}
                                                                        </Alert>
                                                                    </Collapse>
                                                                </Box>
                                                            )}
                                                            <Stack direction="row" spacing={2} mt={0.5}>
                                                                {notif.whanotretrycount > 0 && (
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        Reintentos: {notif.whanotretrycount}
                                                                    </Typography>
                                                                )}
                                                                {notif.whanotmessageid && (
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        ID: {notif.whanotmessageid}
                                                                    </Typography>
                                                                )}
                                                            </Stack>
                                                        </Box>
                                                    }
                                                />
                                            </Stack>
                                        </ListItem>
                                        {index < notifications.length - 1 && <Divider variant="inset" component="li" />}
                                    </React.Fragment>
                                );
                            })}
                        </List>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={onClose}>Cerrar</Button>
                    <Button variant="contained" color="primary" startIcon={<HistoryIcon />}
                        onClick={handleRetryAll} disabled={retrying || loading || getFailedCount() === 0}>
                        Reenviar todas las fallidas
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={!!successMessage} autoHideDuration={6000} onClose={() => setSuccessMessage(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity="success" onClose={() => setSuccessMessage(null)}>{successMessage}</Alert>
            </Snackbar>
            <Snackbar open={!!localError} autoHideDuration={6000} onClose={() => setLocalError(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity="error" onClose={() => setLocalError(null)}>{localError}</Alert>
            </Snackbar>
        </>
    );
};

export default PassengerNotificationHistory;